import React from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

/**
 * SpendingDistributionChart Component
 * 
 * Shows a visual breakdown of transactions by spending ranges
 * Helps users understand their spending patterns (many small vs few large purchases)
 */
const SpendingDistributionChart = ({ transactions = [], tooltip = null }) => {
  // Define spending ranges
  const ranges = [
    { label: '$0-$10', min: 0, max: 10, color: 'bg-emerald-500' },
    { label: '$10-$50', min: 10.01, max: 50, color: 'bg-green-500' },
    { label: '$50-$100', min: 50.01, max: 100, color: 'bg-yellow-500' },
    { label: '$100-$500', min: 100.01, max: 500, color: 'bg-orange-500' },
    { label: '$500-$1K', min: 500.01, max: 1000, color: 'bg-red-500' },
    { label: '$1K+', min: 1000.01, max: Infinity, color: 'bg-purple-600' }
  ];

  // Calculate distribution
  const distribution = ranges.map(range => {
    const count = transactions.filter(tx => {
      const amount = Math.abs(tx.amount || 0);
      return amount >= range.min && amount <= range.max;
    }).length;

    return {
      ...range,
      count,
      percentage: transactions.length > 0 ? (count / transactions.length) * 100 : 0
    };
  });

  // Find max count for scaling bars
  const maxCount = Math.max(...distribution.map(d => d.count), 1);

  // Calculate total for display
  const totalTransactions = transactions.length;

  return (
    <div className="relative bg-gradient-to-br from-white via-white to-gray-50/95 shadow-[0_8px_25px_-8px_rgba(0,0,0,0.3)] hover:shadow-[0_12px_35px_-8px_rgba(0,0,0,0.4)] rounded-xl p-2 sm:p-3 lg:p-4 border border-white/50 backdrop-blur-xl transition-all duration-300 hover:translate-y-[-2px] min-h-[100px] sm:min-h-[120px] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 sm:mb-3 flex-shrink-0">
        <h3 className="text-xs sm:text-sm font-medium text-gray-600 truncate flex-1">
          Spending Distribution
        </h3>
        {tooltip && (
          <div className="relative group flex-shrink-0">
            <InformationCircleIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
            <div className="absolute bottom-full right-0 mb-2 w-64 bg-gray-800 text-white text-xs rounded-lg p-3 shadow-lg invisible group-hover:visible transition-all pointer-events-none z-10">
              <p className="font-normal leading-relaxed">
                {tooltip}
              </p>
              <div className="absolute top-full right-4 border-8 border-transparent border-t-gray-800"></div>
            </div>
          </div>
        )}
      </div>

      {/* Total */}
      <div className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-700 mb-2 sm:mb-3">
        {totalTransactions} transactions
      </div>

      {/* Distribution Bars */}
      <div className="flex-1 flex flex-col justify-center space-y-1.5 sm:space-y-2">
        {distribution.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            {/* Label */}
            <div className="w-14 sm:w-16 text-xs text-gray-600 font-medium flex-shrink-0">
              {item.label}
            </div>
            
            {/* Bar */}
            <div className="flex-1 h-5 sm:h-6 bg-gray-100 rounded-full overflow-hidden relative">
              <div
                className={`h-full ${item.color} transition-all duration-500 ease-out flex items-center justify-end pr-2`}
                style={{ width: `${(item.count / maxCount) * 100}%` }}
              >
                {item.count > 0 && (
                  <span className="text-xs font-medium text-white drop-shadow-sm">
                    {item.count}
                  </span>
                )}
              </div>
            </div>
            
            {/* Percentage */}
            <div className="w-10 sm:w-12 text-xs text-gray-500 text-right flex-shrink-0">
              {item.percentage.toFixed(0)}%
            </div>
          </div>
        ))}
      </div>

      {/* Legend/Summary */}
      <div className="mt-2 sm:mt-3 pt-2 border-t border-gray-200 text-xs text-gray-500">
        {distribution.filter(d => d.count > 0).length} of {ranges.length} ranges active
      </div>
    </div>
  );
};

export default SpendingDistributionChart;

